import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { HelpCircle } from "lucide-react";
import { useState, useRef, useEffect } from "react";

export const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showHelpDropdown, setShowHelpDropdown] = useState(false);
  const helpDropdownRef = useRef(null);

  const handlePricingClick = () => {
    // If already on the landing page, scroll smoothly to pricing
    if (location.pathname === "/") {
      const section = document.getElementById("pricing");
      if (section) section.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#pricing");
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (helpDropdownRef.current && !helpDropdownRef.current.contains(event.target)) {
        setShowHelpDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="navbar">
      <div className="nav-container">
        {/* Brand */}
        <Link to="/" className="logo">
          <img src="/showme-logo.png" alt="SHOWME" className="logo-icon" />
          <div className="logo-text">
            <div className="logo-brand">SHOWME</div>
            <div className="logo-subtitle">Campaign Finances</div>
          </div>
        </Link>

        {/* Links */}
        <nav className="nav-links">
          {!isAuthenticated ? (
            <>
              <button
                onClick={handlePricingClick}
                className="nav-item nav-button-link"
              >
                Pricing
              </button>
              <Link to="/login" className="nav-item">
                Login
              </Link>
              <Link to="/signup" className="btn-primary small">
                Get Started
              </Link>
            </>
          ) : (
            <>
              <Link to="/dashboard" className="nav-item">
                Dashboard
              </Link>
              {user?.tier !== "free" && (
                <Link to="/analytics" className="nav-item">
                  Analytics
                </Link>
              )}
              <Link
                to="/account/pricing"
                className="nav-item"
                style={{ position: 'relative' }}
              >
                Upgrade
                {user?.tier === "free" && (
                  <span style={{
                    fontSize: '0.7rem',
                    color: '#2563eb',
                    marginLeft: '4px',
                    fontWeight: 600
                  }}>
                    • Unlock Analytics
                  </span>
                )}
              </Link>

              {/* Help Dropdown */}
              <div style={{ position: 'relative' }} ref={helpDropdownRef}>
                <button
                  onClick={() => setShowHelpDropdown(!showHelpDropdown)}
                  className="nav-item"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                  title="Help"
                >
                  <HelpCircle size={20} />
                </button>

                {showHelpDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '200px',
                    zIndex: 1000,
                    overflow: 'hidden'
                  }}>
                    <Link
                      to="/about"
                      style={{
                        display: 'block',
                        padding: '0.75rem 1rem',
                        color: '#374151',
                        textDecoration: 'none',
                        fontSize: '0.95rem',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => setShowHelpDropdown(false)}
                    >
                      About
                    </Link>
                    <Link
                      to="/methodology"
                      style={{
                        display: 'block',
                        padding: '0.75rem 1rem',
                        color: '#374151',
                        textDecoration: 'none',
                        fontSize: '0.95rem',
                        transition: 'background-color 0.2s',
                        borderTop: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => setShowHelpDropdown(false)}
                    >
                      Methodology
                    </Link>
                    <Link
                      to="/faq"
                      style={{
                        display: 'block',
                        padding: '0.75rem 1rem',
                        color: '#374151',
                        textDecoration: 'none',
                        fontSize: '0.95rem',
                        transition: 'background-color 0.2s',
                        borderTop: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => setShowHelpDropdown(false)}
                    >
                      FAQ
                    </Link>
                    <a
                      href="mailto:support@showmecampaignfinances.com"
                      style={{
                        display: 'block',
                        padding: '0.75rem 1rem',
                        color: '#374151',
                        textDecoration: 'none',
                        fontSize: '0.95rem',
                        transition: 'background-color 0.2s',
                        borderTop: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => setShowHelpDropdown(false)}
                    >
                      Contact Support
                    </a>
                  </div>
                )}
              </div>

              <button onClick={logout} className="btn-primary small">
                Logout
              </button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};
